/**
 * Cold-start timing marks (docs/cold-start-optimization-plan.md, Phase A). No Consumer
 * equivalent.
 *
 * Each milestone is a `performance.mark('jiopc:<name>')`, so a Performance-panel recording of a
 * cold launch shows the app's own steps on the timeline next to Chrome's. Times are read back
 * relative to the navigation start (`performance.now()` origin), so the first number already
 * includes Chrome's start-up, the service worker and the `main.js` download.
 *
 * Only the first occurrence of each milestone counts: this measures the cold start, not later
 * re-runs (a second Dashboard visit, a resume refresh). Pure module state, no DI, so the earliest
 * marks in `main.ts` work before Angular exists.
 */

/** Startup milestones, in the order they normally happen. */
export const StartupMilestones = {
  /** `main.ts` started executing — everything before this is Chrome, SW and the main.js load. */
  BOOT_START: 'boot_start',
  /** `bootstrapApplication` resolved: the root shell is created and has rendered once. */
  APP_BOOTSTRAPPED: 'app_bootstrapped',
  /** Maintenance / device / browser / install gates passed. */
  GATES_PASSED: 'gates_passed',
  /** Encrypted storage decrypted into memory. */
  STORAGE_HYDRATED: 'storage_hydrated',
  /** Startup session gate finished (silent refresh included, when one was needed). */
  SESSION_VALIDATED: 'session_validated',
  /** `authStore.hydrate()` ran — guards can now evaluate. */
  AUTH_HYDRATED: 'auth_hydrated',
  /** The first navigation finished and a screen is in the router outlet. */
  FIRST_ROUTE: 'first_route',
  /** Dashboard has profile + subscriptions (or gave up on them) and stops skeletoning. */
  DASHBOARD_READY: 'dashboard_ready',
} as const;

export type StartupMilestone = (typeof StartupMilestones)[keyof typeof StartupMilestones];

/** Facts about this start that explain its timings (what ran, not how long it took). */
export interface StartupContext {
  /** Startup session-gate outcome (`TokenRefreshService.validateSession`), e.g. `refreshed`. */
  session?: string;
  /** Path of the first screen the router activated, without query or hash. */
  first_route?: string;
  /** Opened by the Windows launcher at sign-in (`?launcher=1`), not by the user. */
  launcher?: boolean;
}

const MARK_PREFIX = 'jiopc:';

const marks = new Map<StartupMilestone, number>();
let context: StartupContext = {};

/** Adds facts to the startup context; the first value for each field wins, like the marks. */
export function noteStartupContext(facts: StartupContext): void {
  context = { ...facts, ...context };
}

export function startupContext(): StartupContext {
  return { ...context };
}

/** Records a milestone once; later calls for the same milestone are ignored. */
export function markStartup(milestone: StartupMilestone): void {
  if (marks.has(milestone)) return;
  const perf = typeof performance === 'undefined' ? undefined : performance;
  const at = perf?.now() ?? 0;
  marks.set(milestone, at);
  try {
    perf?.mark?.(`${MARK_PREFIX}${milestone}`);
  } catch {
    // User Timing missing or rejected the name — the in-memory time is enough.
  }
}

/** Milestone → whole ms since navigation start, in the order they were recorded. */
export function startupTimings(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [milestone, at] of marks) out[milestone] = Math.round(at);
  return out;
}

export function hasStartupMilestone(milestone: StartupMilestone): boolean {
  return marks.has(milestone);
}

/** Test-only: forget every recorded milestone and the context. */
export function resetStartupTimingsForTest(): void {
  marks.clear();
  context = {};
}
